// ✅ FILE: src/pages/WhatWeDo.jsx
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

import { focusAreas, WHAT_WE_DO_BASE } from "./what-we-do/focus-areas";

export const WhatWeDo = () => {
  return (
    <div data-testid="what-we-do-page" className="bg-white text-stone-900">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-36 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/whatwedo-hero.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/90 via-blue-950/75 to-black/70" />

        <div className="absolute -top-20 -left-20 w-[280px] h-[280px] rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-[260px] h-[260px] rounded-full bg-cyan-400/15 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            {/* ✅ HERO CHIP: glass + white text (no black text) */}
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold tracking-wide backdrop-blur-sm mb-6 text-white/95 shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
              <Sparkles className="w-4 h-4 text-amber-300" />
              Our Work
            </span>

            {/* ✅ HERO TITLE: white + gold accent */}
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-white drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)]">
              What We Do{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400">
                at GLGIF
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed drop-shadow-[0_14px_38px_rgba(0,0,0,0.45)]">
              GLGIF works across four core focus areas designed to protect the
              vulnerable, expand opportunity, strengthen livelihoods, and build
              resilient communities.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 shadow-xl hover:shadow-2xl transition-all"
              >
                <Link to="/get-involved#donate">
                  Support Our Work <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>

              <Button
                asChild
                variant="outline"
                className="border-white/30 bg-white/10 text-white hover:bg-white/15 hover:text-white rounded-full backdrop-blur"
              >
                <Link to="/projects">View Projects</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Intro strip */}
      <section className="py-14 md:py-16 bg-gradient-to-b from-stone-50 to-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              "Emergency response with dignity",
              "Sustainable empowerment pathways",
              "Community-led measurable impact",
            ].map((item, idx) => (
              <div
                key={item}
                className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <p className="text-stone-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus Areas (✅ SHOULD SHOW 4) */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-accent text-xl text-amber-700 mb-3">
              Our Focus Areas
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">
              Designed for Real, Lasting Change
            </h2>
          </div>

          <div className="space-y-8">
            {focusAreas.map((area, index) => {
              const reverse = index % 2 === 1;
              const detailLink = `${WHAT_WE_DO_BASE}/${area.slug}`;

              return (
                <Card
                  key={area.key || area.slug}
                  className={`overflow-hidden border ${area.borderColor} rounded-3xl shadow-sm hover:shadow-lg transition-all`}
                  data-testid={`focus-area-detail-${index}`}
                >
                  <CardContent className="p-0">
                    <div
                      className={`grid lg:grid-cols-2 items-stretch ${
                        reverse ? "lg:[&>*:first-child]:order-2" : ""
                      }`}
                    >
                      {/* Image */}
                      <div className="relative min-h-[260px] lg:min-h-[320px]">
                        <img
                          src={area.image}
                          alt={area.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-black/35 via-black/10 to-transparent" />
                      </div>

                      {/* Content */}
                      <div className="p-7 md:p-10 flex flex-col justify-center">
                        <div className="flex items-start gap-4 mb-5">
                          <div
                            className={`w-14 h-14 ${area.color} rounded-2xl flex items-center justify-center ring-8 ${area.iconRing} shrink-0`}
                          >
                            <area.icon className="w-7 h-7" />
                          </div>

                          <div className="min-w-0">
                            {/* ✅ Label chip for this section (safe colors on white bg) */}
                            <div className="mb-2">
                              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${area.listBadge}`}>
                                {area.shortLabel}
                              </span>
                            </div>

                            <h3 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 leading-tight">
                              {area.title}
                            </h3>
                          </div>
                        </div>

                        <p className="text-stone-600 text-base md:text-lg leading-relaxed mb-6">
                          {area.description}
                        </p>

                        <div className="flex items-center gap-2 text-stone-700 mb-6">
                          <CheckCircle2 className="w-5 h-5 text-amber-700" />
                          <span className="text-sm md:text-base">
                            Practical support, measurable outcomes, people-first
                            approach.
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            asChild
                            className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-7"
                          >
                            <Link to={detailLink}>
                              Learn More{" "}
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                          </Button>

                          <Button asChild variant="outline" className="rounded-full">
                            <Link to="/get-involved#donate">Donate</Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeDo;